import { Link, Typography } from '@loomstateorg/shared-frontend';
import Button from 'components/commercetools-ui/Button';
import getFontSize from '@loomstateorg/shared-frontend/utils/getFontSize';
import { Reference } from 'types/reference';
import Image from 'frontastic/lib/image';
import {
  CAVA_CONTENT_LOOK,
  ContentLook,
} from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content-look';

export interface HeroProps {
  image?: string;
  videoUrl?: string;
  videoMimeType?: string;
  title?: string;
  titleColor?: string;
  subtitle?: string;
  subtitleColor?: string;
  ctaLabel?: string;
  ctaReference?: Reference;
  buttonBackgroundColor?: string;
  buttonBackgroundColorHover?: string;
  buttonBackgroundTransparent?: boolean;
  buttonBorderRadius?: 'sm' | 'md' | 'lg' | 'xxl';
  buttonHorizontalAlign?: 'left' | 'center' | 'right';
  buttonVerticalAlign?: 'top' | 'center' | 'bottom';
  buttonTextColor?: string;
  titleFontSize?: string;
  subtitleFontSize?: string;
  buttonFontSize?: string;
  contentDirection?: 'row' | 'row-reverse';
  look?: Pick<
    ContentLook,
    'heroTitleFontFamily' | 'heroTitleMarginClassName' | 'heroButtonClassMap' | 'heroButtonTextClassName'
  >;
  children?: React.ReactNode;
}

const Hero: React.FC<HeroProps> = ({
  image,
  videoUrl,
  videoMimeType,
  title,
  titleColor,
  subtitle,
  subtitleColor,
  ctaLabel,
  ctaReference,
  buttonBackgroundColor,
  buttonBackgroundColorHover,
  buttonBackgroundTransparent,
  buttonBorderRadius = 'none',
  buttonHorizontalAlign = 'center',
  buttonVerticalAlign = 'center',
  buttonTextColor,
  titleFontSize = '1x',
  subtitleFontSize = '1x',
  buttonFontSize = '1x',
  contentDirection = 'row',
  look = CAVA_CONTENT_LOOK,
}) => {
  const getHorizontalAlignClass = () => {
    const alignClasses = {
      left: 'justify-start',
      right: 'justify-end',
      center: 'justify-center',
    };

    return alignClasses[buttonHorizontalAlign];
  };

  const getVerticalAlignClass = () => {
    const alignClasses = {
      top: 'top-0 translate-y-0',
      bottom: 'bottom-0 translate-y-0',
      center: 'top-1/2 -translate-y-1/2',
    };

    return alignClasses[buttonVerticalAlign];
  };

  const getContentDirectionClass = () => {
    const directionClasses = {
      row: 'flex-col',
      'row-reverse': 'flex-col-reverse',
    } as const;

    return directionClasses[contentDirection] || 'flex-col';
  };

  const getButtonState = () => {
    if (buttonBackgroundTransparent) {
      return 'transparent';
    }

    if (!buttonBackgroundColor && !buttonBackgroundColorHover) {
      return 'default';
    }

    if (!buttonBackgroundColor && buttonBackgroundColorHover) {
      return 'withHover';
    }

    if (buttonBackgroundColor && !buttonBackgroundColorHover) {
      return 'withBackground';
    }

    return 'default';
  };


  const getButtonClasses = () => {
    let classes = 'md:px-48 md:py-12 transition-all duration-200';

    const hasContent = title || subtitle;

    if (hasContent) {
      classes += ' mt-22 md:mt-36 lg:mt-32';
    } else {
      const verticalAlignClasses = {
        top: 'mt-22 md:mt-36 lg:mt-32',
        bottom: 'mb-22 md:mb-36 lg:mb-32',
        center: 'my-22 md:my-36 lg:my-32',
      };

      classes += ` ${verticalAlignClasses[buttonVerticalAlign] || verticalAlignClasses.center}`;

      const horizontalAlignClasses = {
        left: 'ml-0',
        right: 'mr-0',
        center: 'mx-auto',
      };

      classes += ` ${horizontalAlignClasses[buttonHorizontalAlign] || horizontalAlignClasses.center}`;
    }

    const classMap: Record<string, string> = {
      transparent: 'bg-transparent border-0 hover:bg-transparent hover:opacity-100',
      ...look.heroButtonClassMap,
    };

    classes += ` ${classMap[getButtonState()]}`;


    return classes;
  };

  // Tailwind not supporting dynamic class names
  const getButtonStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (buttonBackgroundTransparent) {
      styles.backgroundColor = 'transparent';
      styles.border = 'none';
      return styles;
    }

    if (buttonBackgroundColor) {
      styles.backgroundColor = buttonBackgroundColor;
    }

    return styles;
  };

  const getButtonTextStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (buttonTextColor) {
      styles.color = buttonTextColor;
    }

    return styles;
  };

  const getTitleStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (titleColor) {
      styles.color = titleColor;
    }

    return styles;
  };

  const getSubtitleStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (subtitleColor) {
      styles.color = subtitleColor;
    }

    return styles;
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonBackgroundTransparent) {
      return;
    }

    if (buttonBackgroundColorHover) {
      e.currentTarget.style.backgroundColor = buttonBackgroundColorHover;
    } else if (buttonBackgroundColor) {
      e.currentTarget.style.backgroundColor = '';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonBackgroundTransparent) {
      return;
    }

    if (buttonBackgroundColor) {
      e.currentTarget.style.backgroundColor = buttonBackgroundColor;
    } else {
      e.currentTarget.style.backgroundColor = '';
    }
  };

  return (
    <section data-component-group="hero" data-testid="hero-section" className="relative col-span-8 col-start-2 w-full overflow-hidden">
      {videoUrl ? (
        <div className="relative h-296 md:h-[532px] lg:h-[668px]">
          <video
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
          >
            <source src={videoUrl} type={videoMimeType ?? 'video/mp4'} />
          </video>
        </div>
      ) : (
        image && (
          <div className="relative h-296 md:h-[532px] lg:h-[668px]">
            <Image data-testid="hero-background-image" src={image} fill={true} style={{ objectFit: 'cover' }} priority={true} alt="Hero image" />
          </div>
        )
      )}
      <div className={`absolute left-0 w-full p-48 ${getVerticalAlignClass()}`}>
        <div className={`flex ${getHorizontalAlignClass()} w-full`}>
          <div className={`flex ${getContentDirectionClass()} max-w-md items-center text-center`}>
            {subtitle && (
              <Typography
                data-testid="hero-subtitle"
                align="center"
                className={`text-${getFontSize(14, subtitleFontSize)} md:text-${getFontSize(18, subtitleFontSize)} ${subtitleColor ? '' : 'text-white'}`}
                style={getSubtitleStyles()}
              >
                {subtitle}
              </Typography>
            )}
            {title && (
              <Typography
                data-testid="hero-title"
                align="center"
                className={`${look.heroTitleMarginClassName} text-${getFontSize(27, titleFontSize)} md:text-${getFontSize(44, titleFontSize)} ${titleColor ? '' : 'text-white'}`}
                fontFamily={look.heroTitleFontFamily}
                style={getTitleStyles()}
              >
                {title}
              </Typography>
            )}
            {ctaLabel && (
              <Link link={ctaReference} data-testid="hero-cta">
                <Button
                  data-testid="hero-cta-button"
                  tabIndex={-1}
                  className={getButtonClasses()}
                  style={getButtonStyles()}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <Typography
                    fontFamily="secondaryBold"
                    className={`text-${getFontSize(16, buttonFontSize)} md:text-${getFontSize(20, buttonFontSize)} uppercase ${buttonTextColor ? '' : look.heroButtonTextClassName}`}
                    style={getButtonTextStyles()}
                  >
                    {ctaLabel}
                  </Typography>
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
